import React from "react";

import type { UpsertDependentPayload } from "../api/employeesApi";
import { DEPENDENT_RELATIONSHIP_LABELS, DpDependentRelationship } from "../api/dpTypes";
import { maskCpfInput } from "../utils/format";

/**
 * Linhas repetíveis de filhos/dependentes.
 *
 * Usado em dois lugares: no modal "Novo dependente" da ficha (vários de uma vez,
 * salvos juntos pelo `POST .../dependents/bulk`) e no cadastro de colaborador novo
 * (enviados no próprio `POST /dp/employees`). Nos dois casos o backend grava tudo
 * ou nada — por isso a validação aqui é só a do formulário; a regra é do servidor.
 */

export type DependentRow = {
  /** Chave local da linha — não vai para o backend. */
  key: string;
  fullName: string;
  birthDate: string;
  relationship: DpDependentRelationship;
  cpf: string;
  isIrDependent: boolean;
  isSalarioFamiliaEligible: boolean;
  hasDisability: boolean;
  isUniversityStudent: boolean;
};

let rowSeq = 0;

export const emptyDependentRow = (): DependentRow => ({
  key: `dep-${Date.now()}-${rowSeq++}`,
  fullName: "",
  birthDate: "",
  relationship: "FILHO",
  cpf: "",
  isIrDependent: false,
  isSalarioFamiliaEligible: false,
  hasDisability: false,
  isUniversityStudent: false,
});

/** Linha em que nada foi digitado — ignorada no envio em vez de virar erro. */
export const isBlankDependentRow = (row: DependentRow): boolean =>
  !row.fullName.trim() && !row.birthDate && !row.cpf.trim();

/** Primeiro problema de preenchimento encontrado, ou `null`. Numera a partir de 1. */
export const validateDependentRows = (rows: DependentRow[]): string | null => {
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    if (isBlankDependentRow(row)) continue;
    if (!row.fullName.trim() || !row.birthDate) {
      return `Dependente ${i + 1}: informe nome e data de nascimento.`;
    }
  }
  return null;
};

export const toDependentPayload = (row: DependentRow): UpsertDependentPayload => ({
  fullName: row.fullName.trim(),
  birthDate: row.birthDate,
  relationship: row.relationship,
  isIrDependent: row.isIrDependent,
  isSalarioFamiliaEligible: row.isSalarioFamiliaEligible,
  hasDisability: row.hasDisability,
  isUniversityStudent: row.isUniversityStudent,
  ...(row.cpf.trim() ? { cpf: row.cpf.trim() } : {}),
});

type Props = {
  rows: DependentRow[];
  onChange: (rows: DependentRow[]) => void;
  disabled?: boolean;
  /** `false` no modo edição: uma linha só, sem adicionar/remover. */
  allowMultiple?: boolean;
  /** Limite do backend por envio. */
  max?: number;
  /**
   * `eco` = visual do redesign (telas já migradas). Sem a prop, as classes
   * antigas do `Dp.css`, para a ficha, que ainda não migrou.
   */
  variant?: "legacy" | "eco";
};

const CLASSES = {
  legacy: {
    rows: "dpDependentRows",
    row: "dpDependentRow",
    header: "dpDependentRow__header",
    title: "dpDependentRow__title",
    removeBtn: "dpBtn dpBtn--secondary dpBtn--small",
    grid: "dpFormGrid",
    wideGroup: "dpFormGroup dpFormGrid--wide",
    group: "dpFormGroup",
    input: "dpInput",
    select: "dpSelect",
    hint: "dpFormHint",
    checks: "dpCheckboxRow",
    addBtn: "dpBtn dpBtn--secondary",
  },
  eco: {
    rows: "ecoDeps",
    row: "ecoDeps__row",
    header: "ecoDeps__header",
    title: "ecoDeps__title",
    removeBtn: "ecoBtn ecoBtn--sm ecoBtn--danger",
    grid: "ecoDeps__grid",
    wideGroup: "ecoField ecoDeps__wide",
    group: "ecoField",
    input: "ecoInput ecoInput--sm",
    select: "ecoSelect ecoSelect--sm",
    hint: "ecoDeps__hint",
    checks: "ecoDeps__checks",
    addBtn: "ecoBtn ecoBtn--sm",
  },
};

export default function DependentRowsEditor({
  rows,
  onChange,
  disabled,
  allowMultiple = true,
  max = 20,
  variant = "legacy",
}: Props) {
  const C = CLASSES[variant];
  const update = (key: string, patch: Partial<DependentRow>) =>
    onChange(rows.map((row) => (row.key === key ? { ...row, ...patch } : row)));

  const remove = (key: string) => onChange(rows.filter((row) => row.key !== key));

  return (
    <div className={C.rows}>
      {rows.map((row, index) => {
        const id = (field: string) => `${row.key}-${field}`;
        return (
          <fieldset key={row.key} className={C.row} disabled={disabled}>
            {allowMultiple ? (
              <div className={C.header}>
                <span className={C.title}>Dependente {index + 1}</span>
                {rows.length > 1 ? (
                  <button
                    type="button"
                    className={C.removeBtn}
                    onClick={() => remove(row.key)}
                  >
                    Remover
                  </button>
                ) : null}
              </div>
            ) : null}

            <div className={C.grid}>
              <div className={C.wideGroup}>
                <label htmlFor={id("name")}>Nome completo *</label>
                <input
                  id={id("name")}
                  className={C.input}
                  maxLength={200}
                  value={row.fullName}
                  onChange={(e) => update(row.key, { fullName: e.target.value })}
                />
              </div>

              <div className={C.group}>
                <label htmlFor={id("birth")}>Data de nascimento *</label>
                <input
                  id={id("birth")}
                  type="date"
                  className={C.input}
                  value={row.birthDate}
                  onChange={(e) => update(row.key, { birthDate: e.target.value })}
                />
              </div>

              <div className={C.group}>
                <label htmlFor={id("rel")}>Parentesco *</label>
                <select
                  id={id("rel")}
                  className={C.select}
                  value={row.relationship}
                  onChange={(e) =>
                    update(row.key, { relationship: e.target.value as DpDependentRelationship })
                  }
                >
                  {(Object.keys(DEPENDENT_RELATIONSHIP_LABELS) as DpDependentRelationship[]).map(
                    (r) => (
                      <option key={r} value={r}>
                        {DEPENDENT_RELATIONSHIP_LABELS[r]}
                      </option>
                    )
                  )}
                </select>
              </div>

              <div className={C.group}>
                <label htmlFor={id("cpf")}>CPF</label>
                <input
                  id={id("cpf")}
                  className={C.input}
                  inputMode="numeric"
                  value={row.cpf}
                  onChange={(e) => update(row.key, { cpf: maskCpfInput(e.target.value) })}
                />
                <span className={C.hint}>Opcional — recém-nascido pode ainda não ter.</span>
              </div>
            </div>

            <div className={C.checks}>
              <label>
                <input
                  type="checkbox"
                  checked={row.isSalarioFamiliaEligible}
                  onChange={(e) => update(row.key, { isSalarioFamiliaEligible: e.target.checked })}
                />{" "}
                Salário-família
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={row.isIrDependent}
                  onChange={(e) => update(row.key, { isIrDependent: e.target.checked })}
                />{" "}
                Dependente para IRRF
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={row.hasDisability}
                  onChange={(e) => update(row.key, { hasDisability: e.target.checked })}
                />{" "}
                Pessoa com deficiência
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={row.isUniversityStudent}
                  onChange={(e) => update(row.key, { isUniversityStudent: e.target.checked })}
                />{" "}
                Universitário
              </label>
            </div>
          </fieldset>
        );
      })}

      {allowMultiple && rows.length < max ? (
        <button
          type="button"
          className={C.addBtn}
          style={{ alignSelf: "flex-start" }}
          onClick={() => onChange([...rows, emptyDependentRow()])}
          disabled={disabled}
        >
          + Adicionar outro filho
        </button>
      ) : null}
    </div>
  );
}
